import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileVideo, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import UploadManagerWithRecaptcha from '@/utils/uploadWithRecaptcha';
import { generateSmartVideoId } from '@/utils/videoIdGenerator';
import VideoPreview from './VideoPreview';
import FullPlayer from './FullPlayer';
import PreviewProgress from './PreviewProgress';
import { LanguageSelectionModal } from './LanguageSelectionModal';
import { translateSrt, translateVtt } from '@/utils/apiWithRecaptcha';
import { useVideoStatus } from '@/hooks/useVideoStatus';
import { useRecaptcha } from '@/contexts/RecaptchaContext';
import GoogleRecaptcha from './GoogleRecaptcha';
import { buildApiUrl } from '@/config/env';

// 🚀 Optimized interface - removed unused properties
interface UploadState {
  status: 'idle' | 'uploading' | 'processing' | 'preview_completed' | 'full_processing' | 'completed' | 'error';
  progress: number;
  videoId?: string;
  jobId?: string;
  originalFilename?: string;
  error?: string;
  srtUrl?: string;
  videoUrl?: string;
  fullVideoUrl?: string;
  fullSrtUrl?: string;
}

export default function FileUpload() {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullProcessingRef = useRef<HTMLDivElement>(null);
  
  // 🚀 Consolidated state management
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0
  });

  // reCAPTCHA state
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaScriptLoaded, setRecaptchaScriptLoaded] = useState(false);
  const { setRecaptchaLoaded: setGlobalRecaptchaLoaded, isLoaded: recaptchaApiLoaded, executeRecaptcha } = useRecaptcha();

  // 🚀 Optimized state - combined related states
  const [uiState, setUiState] = useState({
    hasClickedFullProcessing: false,
    isStartingFull: false,
    showStartFromBeginModal: false,
    selectedFile: null as File | null,
    selectedSourceLanguage: 'auto',
    selectedTargetLanguage: '',
    translatedSrtUrl: undefined as string | undefined,
    translatedVttUrl: undefined as string | undefined, // 🚀 NEW: Translated VTT URL
    isTranslating: false,
    forceTranslationUpdate: 0,
    // 🚀 NEW: Object URL for local video playback (Video.js supports this!)
    localVideoUrl: undefined as string | undefined,
    localVideoType: undefined as string | undefined  // 🚀 NEW: MIME type
  });

  // 🚀 Memoized values to prevent unnecessary re-renders
  const selectedFile = uiState.selectedFile;
  const selectedTargetLanguage = uiState.selectedTargetLanguage;
  const selectedSourceLanguage = uiState.selectedSourceLanguage;
  const translatedSrtUrl = uiState.translatedSrtUrl;
  const translatedVttUrl = uiState.translatedVttUrl; // 🚀 NEW
  const isTranslating = uiState.isTranslating;
  const hasClickedFullProcessing = uiState.hasClickedFullProcessing;
  const isStartingFull = uiState.isStartingFull;
  const showStartFromBeginModal = uiState.showStartFromBeginModal;

  const lastTranslatedFromRef = useRef<{ preview?: string | null; full?: string | null }>({ preview: null, full: null });
  
  // 🚀 NEW: Helper to guess MIME from file extension
  const guessMimeFromExt = useCallback((name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'webm') return 'video/webm';
    if (ext === 'ogg' || ext === 'ogv') return 'video/ogg';
    if (ext === 'mp4' || ext === 'm4v') return 'video/mp4';
    if (ext === 'mov') return 'video/quicktime';
    if (ext === 'mkv') return 'video/x-matroska';
    if (ext === 'avi') return 'video/x-msvideo';
    return 'video/mp4'; // fallback
  }, []);

  // 🚀 NEW: Create Object URL from local file for video playback (Video.js supports this!)
  const createLocalVideoUrl = useCallback((file: File): string | null => {
    console.log('🎬 FileUpload - Creating Object URL for local video:', file.name);
    
    // 🚀 NEW: Validate file format for Video.js compatibility
    const supportedFormats = ['mp4', 'webm', 'm4v', '3gp', 'ogv', 'mkv', 'avi', 'mov'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (!fileExtension || !supportedFormats.includes(fileExtension)) {
      console.warn('⚠️ FileUpload - File format not supported by Video.js, skipping Object URL:', fileExtension);
      return null;
    }
    
    try {
      const objectUrl = URL.createObjectURL(file);
      console.log('✅ FileUpload - Object URL created:', objectUrl);
      return objectUrl;
    } catch (error) {
      console.error('❌ FileUpload - Failed to create Object URL:', error);
      return null;
    }
  }, []);
  
  // 🚀 NEW: Cleanup Object URL when component unmounts
  useEffect(() => {
    return () => {
      if (uiState.localVideoUrl) {
        console.log('🧹 FileUpload - Cleaning up Object URL:', uiState.localVideoUrl);
        URL.revokeObjectURL(uiState.localVideoUrl);
      }
    };
  }, [uiState.localVideoUrl]);

  // 🚀 Optimized video status hook usage
  const { videoData, startFull, isPolling, stop } = useVideoStatus(uploadState.videoId, uploadState.jobId);
  
  // 🚀 DEBUG: Log useVideoStatus parameters
  useEffect(() => {
    console.log('🔍 FileUpload - useVideoStatus debug:', {
      videoId: uploadState.videoId,
      jobId: uploadState.jobId,
      uploadState: uploadState
    });
  }, [uploadState.videoId, uploadState.jobId, uploadState]);
  const [fullProcessingData, setFullProcessingData] = useState<any>(null);
  const [fullProgress, setFullProgress] = useState<number>(0);

  // 🚀 Function to load existing video data
  const loadExistingVideoData = useCallback(async (videoId: string) => {
    try {
      console.log('🔍 FileUpload - Loading existing data for video:', videoId);
      
      const response = await fetch(`${buildApiUrl('api/job/status')}?video_id=${videoId}`);
      if (response.ok) {
        const existingData = await response.json();
        console.log('📊 FileUpload - Loaded existing data:', existingData);
        
        if (existingData.video_id && (existingData.preview?.status || existingData.full?.status)) {
          console.log('✅ FileUpload - Setting existing video data from URL');
          
          setUploadState(prev => ({
            ...prev,
            videoId: videoId,
            status: existingData.overall_status === 'completed' ? 'completed' : 'processing',
            jobId: existingData.job_id || existingData.preview?.job_id || `preview-${videoId}`, // 🚀 Fallback job ID for preview
            srtUrl: existingData.preview?.s3_srt_url || existingData.full?.s3_srt_url,
            videoUrl: existingData.preview?.s3_video_url || existingData.full?.s3_video_url,
            fullVideoUrl: existingData.full?.s3_video_url,
            fullSrtUrl: existingData.full?.s3_srt_url,
            originalFilename: existingData.original_name || 'Unknown'
          }));
          
          // Update URL to include video_id parameter
          const newUrl = `${window.location.pathname}?video_id=${videoId}`;
          window.history.pushState({}, '', newUrl);
        } else {
          // Video ID exists but no data yet, set basic state
          setUploadState(prev => ({
            ...prev,
            videoId: videoId,
            status: 'processing'
          }));
        }
      }
    } catch (error) {
      console.log('⚠️ FileUpload - Could not load existing data:', error);
      // Set basic state if load fails
      setUploadState(prev => ({
        ...prev,
        videoId: videoId,
        status: 'processing'
      }));
    }
  }, []);

  // 🚀 Check for video_id in URL on component mount and load existing data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const videoIdFromUrl = urlParams.get('video_id');
    
    if (videoIdFromUrl && !uploadState.videoId) {
      console.log('🔄 FileUpload - Found video_id in URL:', videoIdFromUrl);
      
      // Load existing video data if available
      loadExistingVideoData(videoIdFromUrl);
    }
  }, [loadExistingVideoData]);

  // 🚀 Update URL when video ID changes
  useEffect(() => {
    if (uploadState.videoId) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('video_id', uploadState.videoId);
      
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.pushState({}, '', newUrl);
      
      console.log('🔄 FileUpload - Updated URL with video_id:', uploadState.videoId);
    }
  }, [uploadState.videoId]);

  // 🚀 Memoized event handlers to prevent recreation
  const loadRecaptchaOnInteraction = useCallback(async (event: Event) => {
    const target = event.target as Element;
    if (!containerRef.current?.contains(target) || recaptchaLoaded) return;
    
    console.log('🎯 User interaction detected, loading reCAPTCHA...');
    
    try {
      await executeRecaptcha('user_interaction');
      setRecaptchaLoaded(true);
      console.log('✅ reCAPTCHA loaded successfully');
    } catch (error) {
      console.log('⚠️ reCAPTCHA load failed:', error);
    }
  }, [recaptchaLoaded, executeRecaptcha]);

  // 🚀 Optimized reCAPTCHA loading effect
  useEffect(() => {
    const events = ['click', 'keydown', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, loadRecaptchaOnInteraction, { once: true, passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, loadRecaptchaOnInteraction);
      });
    };
  }, [loadRecaptchaOnInteraction]);

  // 🚀 Optimized video data sync effect
  useEffect(() => {
    if (!videoData) return;

    console.log('🔄 FileUpload - Translation effect triggered:', {
      previewStatus: videoData.preview?.status,
      fullStatus: videoData.full?.status,
      selectedTargetLanguage,
      isTranslating,
      lastTranslatedPreview: lastTranslatedFromRef.current?.preview,
      lastTranslatedFull: lastTranslatedFromRef.current?.full
    });

    // 🚀 FIX: Chỉ xử lý translation, không update status phức tạp
    if (videoData.preview?.status === 'completed') {
      const previewSrtUrl = videoData.preview?.s3_srt_url;
      console.log('🔍 Preview translation check:', {
        previewSrtUrl,
        selectedTargetLanguage,
        isTranslating,
        hasSrtUrl: !!previewSrtUrl,
        hasTargetLang: !!selectedTargetLanguage,
        isTargetLangZero: selectedTargetLanguage === '0'
      });
      
      if (selectedTargetLanguage && selectedTargetLanguage !== '0' && !isTranslating && previewSrtUrl) {
        const key = `${previewSrtUrl}|preview`;
        if (lastTranslatedFromRef.current?.preview !== key) {
          console.log('🌐 FileUpload - Starting preview translation:', { key, lastTranslated: lastTranslatedFromRef.current?.preview });
          translateSubtitles(previewSrtUrl, 'preview');
        } else {
          console.log('⏭️ FileUpload - Preview already translated, skipping');
        }
      } else {
        console.log('⏭️ FileUpload - Preview translation skipped:', {
          reason: !selectedTargetLanguage ? 'no targetLang' : selectedTargetLanguage === '0' ? 'targetLang is 0' : isTranslating ? 'already translating' : 'no srtUrl'
        });
      }
    }
    
    if (videoData.full?.status === 'completed') {
      // 🚀 Object URL đã được tạo ngay khi chọn file, không cần tạo lại
      
      const fullSrtUrl = videoData.full?.s3_srt_url;
      if (selectedTargetLanguage && selectedTargetLanguage !== '0' && !isTranslating && fullSrtUrl) {
        const key = `${fullSrtUrl}|full`;
        if (lastTranslatedFromRef.current?.full !== key) {
          console.log('🌐 FileUpload - Starting full translation:', { key, lastTranslated: lastTranslatedFromRef.current?.full });
          translateSubtitles(fullSrtUrl, 'full');
        } else {
          console.log('⏭️ FileUpload - Full already translated, skipping');
        }
      }
    }
  }, [videoData, selectedTargetLanguage, isTranslating, uiState.forceTranslationUpdate]);

  const translateSubtitles = useCallback(async (srtUrl: string, kind: 'preview' | 'full' = 'preview') => {
    console.log(`🔍 translateSubtitles called:`, {
      srtUrl,
      kind,
      selectedTargetLanguage,
      hasSrtUrl: !!srtUrl,
      hasTargetLang: !!selectedTargetLanguage,
      isTargetLangZero: selectedTargetLanguage === '0'
    });
    
    if (!srtUrl || !selectedTargetLanguage || selectedTargetLanguage === '0') {
      console.log(`⏭️ translateSubtitles skipped:`, {
        reason: !srtUrl ? 'no srtUrl' : !selectedTargetLanguage ? 'no targetLang' : 'targetLang is 0'
      });
      return;
    }
    
    const translationKey = `${srtUrl}|${kind}`;
    
    if (lastTranslatedFromRef.current?.[kind] === translationKey) {
      return;
    }
    
    setUiState(prev => ({ ...prev, isTranslating: true }));
    try {
      // 🚀 NEW: Get VTT URL for the same kind (preview or full)
      const vttUrl = kind === 'preview' 
        ? (videoData?.preview as any)?.s3_vtt_url 
        : (videoData?.full as any)?.s3_vtt_url;
      
      console.log(`🌐 ${kind} translation - URLs:`, { srtUrl, vttUrl });
      
      // 🚀 OPTIMIZED: Chỉ gọi 1 API để tiết kiệm chi phí translation
      // API translate-srt sẽ trả về cả SRT và VTT đã được translate
      const translationResult = await translateSrt(
        srtUrl,
        selectedTargetLanguage,
        selectedSourceLanguage === 'auto' ? 'auto' : selectedSourceLanguage,
        uploadState.videoId,
        executeRecaptcha
      );
      
      console.log(`✅ ${kind} translation completed:`, translationResult);
      
      // 🚀 Extract both SRT and VTT URLs from single API response
      const translatedSrtFromApi = translationResult.translated_srt_url || null;
      let translatedVttFromApi = (translationResult as any).translated_vtt_url || null;
      
      // 🚀 FIX: Validate VTT URL format (allow query params)
      if (translatedVttFromApi && !/\.vtt(\?|$)/i.test(translatedVttFromApi)) {
        console.warn(`⚠️ Invalid VTT URL format: ${translatedVttFromApi}`);
        translatedVttFromApi = null; // Reset to null if not valid VTT
      }
      
      // 🚀 FALLBACK: Generate VTT URL from SRT URL if VTT not available
      if (!translatedVttFromApi && translatedSrtFromApi) {
        translatedVttFromApi = translatedSrtFromApi
          .replace('/translated-srt/', '/translated-vtt/')
          .replace(/\.srt(\?.*)?$/i, '.vtt$1');
        console.log(`🔄 Generated VTT URL from SRT: ${translatedVttFromApi}`);
      }
      
      console.log(`🔍 ${kind} translation - Raw API response:`, translationResult);
      console.log(`🔍 ${kind} translation - Extracted URLs:`, {
        translatedSrtFromApi,
        translatedVttFromApi,
        hasVtt: !!translatedVttFromApi,
        vttUrlEndsWithVtt: translatedVttFromApi?.endsWith('.vtt'),
        vttUrlEndsWithSrt: translatedVttFromApi?.endsWith('.srt')
      });
      
      // Update state with both URLs
      setUiState(prev => ({ 
        ...prev, 
        translatedSrtUrl: translatedSrtFromApi || prev.translatedSrtUrl,
        translatedVttUrl: translatedVttFromApi || prev.translatedVttUrl
      }));
      
      lastTranslatedFromRef.current = { ...lastTranslatedFromRef.current, [kind]: translationKey };
    } catch (error) {
      console.error(`❌ ${kind} translation error:`, error);
    } finally {
      setUiState(prev => ({ ...prev, isTranslating: false }));
    }
  }, [selectedTargetLanguage, selectedSourceLanguage, uploadState.videoId, executeRecaptcha, videoData]);

  // 🚀 FIX: Handle immediate translation when language is selected from modal
  useEffect(() => {
    if (!uiState.forceTranslationUpdate || !selectedTargetLanguage || selectedTargetLanguage === '0') return;
    
    console.log('🚀 Language selected from modal, triggering immediate translation:', {
      selectedTargetLanguage,
      forceTranslationUpdate: uiState.forceTranslationUpdate,
      videoData: !!videoData
    });

    // Reset forceTranslationUpdate to prevent infinite loop
    setUiState(prev => ({ ...prev, forceTranslationUpdate: 0 }));

    // If videoData exists, trigger translation immediately
    if (videoData) {
      if (videoData.preview?.status === 'completed' && videoData.preview?.s3_srt_url) {
        console.log('🌐 Immediate preview translation triggered');
        translateSubtitles(videoData.preview.s3_srt_url, 'preview');
      }
      
      if (videoData.full?.status === 'completed' && videoData.full?.s3_srt_url) {
        console.log('🌐 Immediate full translation triggered');
        translateSubtitles(videoData.full.s3_srt_url, 'full');
      }
    }
  }, [uiState.forceTranslationUpdate, selectedTargetLanguage, videoData, translateSubtitles]);


  // 🚀 DEBUG: Log translatedVttUrl changes
  useEffect(() => {
    console.log('🔁 FileUpload - translatedVttUrl changed:', translatedVttUrl);
  }, [translatedVttUrl]);

  // helper: xác định ngữ cảnh full
  const isFullContext = hasClickedFullProcessing || !!uploadState.jobId;

  // 🚀 Optimized scroll effects
  useEffect(() => {
    if (uploadState.status === 'uploading' && containerRef.current) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          const elementTop = containerRef.current.offsetTop;
          window.scrollTo({
            top: elementTop - 40,
            behavior: 'smooth'
          });
        }
      });
    }
  }, [uploadState.status]);

  useEffect(() => {
    const shouldScroll = hasClickedFullProcessing || uploadState.status === 'full_processing' || !!uploadState.jobId;
    if (!shouldScroll) return;

    let retryCount = 0;
    const maxRetries = 10;
    
    const doScroll = () => {
      const el = fullProcessingRef.current;
      if (!el) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(doScroll, 500);
          return;
        }
        return;
      }

      const top = el.getBoundingClientRect().top + window.scrollY - 200;
      window.scrollTo({ top, behavior: 'smooth' });
    };

    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(doScroll);
      return () => cancelAnimationFrame(r2);
    });

    return () => cancelAnimationFrame(r1);
  }, [hasClickedFullProcessing, fullProcessingData?.status, uploadState.jobId]);
  
  // 🚀 FIX: Chỉ dùng useVideoStatus hook để tránh duplicate polling
  // FileUpload component sẽ sync với videoData từ useVideoStatus thay vì tự poll
  
  // 🚀 SIMPLE: Trực tiếp hiện progress bar khi thanh toán hoàn thành
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  
  // 🚀 SIMPLE: Set trạng thái khi thanh toán hoàn thành
  const handlePaymentComplete = useCallback((jobId: string) => {
    console.log('🚀 FileUpload - Payment complete, showing progress immediately:', { jobId });
    setIsPaymentComplete(true);
    setUploadState(prev => ({
      ...prev,
      status: 'full_processing',
      jobId: jobId
    }));
  }, []);
  
  // 🚀 FIX: Đảm bảo cờ không bị reset khi full processing đang chạy
  useEffect(() => {
    if (isPaymentComplete && videoData?.full?.status === 'processing') {
      console.log('🚀 FileUpload - Full processing in progress, keeping flag active');
      // Không reset cờ khi đang processing
    }
  }, [isPaymentComplete, videoData?.full?.status]);
  
  // 🚀 Sync uploadState với videoData từ useVideoStatus hook
  useEffect(() => {
    if (!videoData) return;
    
    // 🚀 FIX: Reset cờ khi full processing thực sự hoàn thành
    if (isPaymentComplete && videoData?.full?.status === 'completed') {
      console.log('🚀 FileUpload - Full processing completed, resetting flag');
      setIsPaymentComplete(false);
    }
    
    console.log('🔄 FileUpload - Syncing with videoData:', videoData);
    
    // 🚀 SIMPLE: Chỉ cập nhật fullProcessingData để UI render đúng
    setFullProcessingData({
      preview: videoData.preview,
      full: videoData.full,
      status: videoData.full?.status || videoData.preview?.status || 'processing'
    });
    
  }, [videoData]);

  // 🚀 Memoized file validation - Check both MIME type and file extension
  const validateFile = useCallback((file: File): string | null => {
    // Check MIME type first
    const isValidMimeType = file.type.startsWith('video/') || file.type.startsWith('audio/');
    
    // Check file extension as fallback (for formats like MKV that might have different MIME types)
    const fileName = file.name.toLowerCase();
    const validExtensions = [
      // Video formats
      '.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm', '.m4v', '.3gp', '.ogv',
      // Audio formats  
      '.wav', '.mp3', '.m4a', '.aac', '.ogg', '.flac', '.wma'
    ];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidMimeType && !hasValidExtension) {
      console.log('❌ File validation failed:', {
        fileName: file.name,
        mimeType: file.type,
        isValidMimeType,
        hasValidExtension
      });
      return 'Please select a valid video or audio file';
    }

    const maxSize = 20 * 1024 * 1024 * 1024; // 20GB
    if (file.size > maxSize) {
      return 'File size must be less than 20GB';
    }

    console.log('✅ File validation passed:', {
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
      isValidMimeType,
      hasValidExtension
    });

    return null;
  }, []);

  const uploadFile = useCallback(async (file: File) => {
    if (!recaptchaApiLoaded) {
      setUploadState({
        status: 'error',
        progress: 0,
        error: 'reCAPTCHA not loaded. Please try again.'
      });
      return;
    }

    let recaptchaToken: string;
    try {
      recaptchaToken = await executeRecaptcha('file_upload');
    } catch (error) {
      setUploadState({
        status: 'error',
        progress: 0,
        error: 'Failed to verify reCAPTCHA. Please try again.'
      });
      return;
    }

    const videoId = await generateSmartVideoId(file);
    
    setUiState(prev => ({ ...prev, hasClickedFullProcessing: false }));
    
    try {
      setUploadState(prev => ({
        ...prev,
        status: 'uploading',
        originalFilename: file.name
      }));

      const uploadManager = new UploadManagerWithRecaptcha();
      
      uploadManager.onProgress((progress) => {
        setUploadState(prev => ({
          ...prev,
          progress: progress.progress || 0
        }));
      });

      const result = await uploadManager.uploadFile(file, videoId, executeRecaptcha);
      
      if (result.jobId) {
        setUploadState({
          status: 'processing',
          progress: 0,
          videoId: videoId,
          jobId: result.jobId,
          originalFilename: file.name
        });
      } else {
        throw new Error('Upload failed - no job ID returned');
      }

    } catch (error) {
      setUploadState({
        status: 'error',
        progress: 0,
        videoId: videoId,
        error: error instanceof Error ? error.message : 'Upload failed'
      });
    }
  }, [recaptchaApiLoaded, executeRecaptcha]);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('🚀 FileUpload - File selected:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileExtension: file.name.split('.').pop()?.toLowerCase()
    });

    const error = validateFile(file);
    if (error) {
      console.log('❌ FileUpload - File validation failed:', error);
      setUploadState({
        status: 'error',
        progress: 0,
        error
      });
      return;
    }

    // 🚀 NEW: Direct upload flow - no language modal, call presigned API immediately
    console.log('🚀 FileUpload - File selected, starting direct upload flow');
    
    if (!recaptchaLoaded) {
      executeRecaptcha('file_upload').then(() => {
        setRecaptchaLoaded(true);
      }).catch(error => {
        console.log('⚠️ reCAPTCHA not ready, will retry during upload:', error);
      });
    }

    // 🚀 NEW: Tạo Object URL NGAY LẬP TỨC và giữ MIME
    const objectUrl = createLocalVideoUrl(file);
    const videoType = file.type || guessMimeFromExt(file.name);
    
    setUiState(prev => ({ 
      ...prev, 
      selectedFile: file, 
      localVideoUrl: objectUrl || undefined,
      localVideoType: videoType
    }));
    
    // 🚀 NEW: Start upload immediately without language modal
    console.log('🚀 FileUpload - Starting upload immediately');
    uploadFile(file);
  }, [recaptchaLoaded, executeRecaptcha, validateFile, uploadFile]);

  const handleRetry = useCallback(() => {
    setUploadState({
      status: 'idle',
      progress: 0,
      videoId: undefined,
      jobId: undefined
    });
    setUiState(prev => ({
      ...prev,
      hasClickedFullProcessing: false,
      isStartingFull: false,
      selectedFile: null,
      showLanguageModal: false,
      translatedSrtUrl: undefined,
      translatedVttUrl: undefined // 🚀 NEW: Clear translated VTT URL
    }));
    lastTranslatedFromRef.current = { preview: null, full: null };
  }, []);

  const handleStartFromBegin = useCallback(() => {
    // Reset to home page and clear video_id from URL
    window.location.href = '/';
  }, []);

  const showStartFromBeginModalHandler = useCallback(() => {
    setUiState(prev => ({ ...prev, showStartFromBeginModal: true }));
  }, []);

  const handleStartFullProcessing = useCallback(async () => {
    if (!uploadState.videoId) {
      console.error('No video ID available for full processing');
      return;
    }

    try {
      setUiState(prev => ({ ...prev, hasClickedFullProcessing: true, isStartingFull: true }));
      
      setUploadState(prev => ({
        ...prev,
        status: 'full_processing'
      }));

      stop();
      
      try {
        await startFull();
      } catch (error) {
        console.error('❌ Error starting full processing via hook:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error starting full processing:', error);
      setUploadState(prev => ({
        ...prev,
        status: 'error',
        error: 'Failed to start full processing'
      }));
    } finally {
      setUiState(prev => ({ ...prev, isStartingFull: false }));
    }
  }, [uploadState.videoId, stop, startFull]);

  // 🚀 Memoized render functions to prevent unnecessary re-renders
  const renderUploadArea = useMemo(() => (
    <div 
      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".mp4,.avi,.mov,.mkv,.wmv,.flv,.webm,.m4v,.3gp,.ogv,.wav,.mp3,.m4a,.aac,.ogg,.flac,.wma,video/*,audio/*,video/x-matroska,video/x-msvideo,video/x-ms-wmv,video/x-flv"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <div className="space-y-4">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
          <Upload className="w-8 h-8 text-blue-600" />
        </div>
        
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Upload Video or Audio File
          </h2>
          <p className="text-gray-600 mb-4">
            Drag and drop your file here, or click to browse
          </p>
          <p className="text-sm text-gray-500 mb-4">
            <strong>Video formats:</strong> MP4, AVI, MOV, MKV, WMV, FLV, WebM, M4V, 3GP, OGV<br/>
            <strong>Audio formats:</strong> WAV, MP3, M4A, AAC, OGG, FLAC, WMA<br/>
            <strong>Max size:</strong> 20GB<br/>
            <span className="text-blue-600 font-medium">💡 All videos will be automatically converted to MP4 for web compatibility</span>
          </p>
          
          {/* Security & Privacy Information */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex flex-col items-center text-center space-y-3">
              <div>
                <h3 className="text-sm font-medium text-green-800 mb-2">
                🔒 Your Privacy & Security Guaranteed
                </h3>
                <ul className="text-xs text-green-700 space-y-1 text-left">
                  <li className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>Auto-delete:</strong> Videos deleted after 8 hours</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span><strong>Hash encryption:</strong> Each file gets unique security key</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span><strong>No permanent storage:</strong> Your content is never saved</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span><strong>Isolated processing:</strong> Complete data separation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          aria-label="Choose video or audio file to upload"
        >
          Choose File
        </button>
      </div>
    </div>
  ), [handleFileSelect]);

  const renderUploadProgress = useMemo(() => {
    // 🚀 FIX: Logic progress bar đơn giản và chính xác
    const isFullProcessing = videoData?.full?.status === 'processing';
    const isPreviewProcessing = videoData?.preview?.status === 'processing' || videoData?.preview?.status === 'initial';
    
    console.log('🎬 renderUploadProgress - Progress logic:', {
      isFullProcessing,
      isPreviewProcessing,
      previewProgress: videoData?.preview?.progress,
      fullProgress: videoData?.full?.progress,
      fullStatus: videoData?.full?.status
    });
    
    // 🚀 TIMING DEBUG: Log when progress bar component is actually created
    if (isPaymentComplete) {
      console.log('⏰ TIMING DEBUG - Progress bar component created at:', new Date().toISOString());
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
          <span className="text-lg font-medium text-gray-900">
            {uploadState.status === 'uploading' ? 'Uploading...' : 'Processing...'}
          </span>
        </div>
        
        {/* 🚀 FIX: Show preview progress khi preview đang processing */}
        {isPreviewProcessing && videoData?.preview?.progress !== undefined ? (
          <div data-progress-section>
            <PreviewProgress
              videoId={videoData?.videoId || ''}
              status={videoData?.preview?.status || 'processing'}
              progress={videoData?.preview?.progress || 0}
            />
          </div>
        ) : (
          /* 🚀 FIX: Show full progress khi full đang processing */
          isFullProcessing ? (
            <div data-progress-section className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-blue-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
                style={{ width: `${videoData?.full?.progress || 0}%` }}
              />
            </div>
          ) : (
            /* Fallback progress bar */
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadState.progress}%` }}
              />
            </div>
          )
        )}

        {/* 🚀 FIX: Show full processing UI khi full đang processing */}
        {isFullProcessing && (
          <div ref={fullProcessingRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Processing Full Video with AI...
              </h3>
              <p className="text-gray-600 mb-4">
                Full video processing in progress... {videoData?.full?.progress || 0}%
              </p>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
                  style={{ width: `${videoData?.full?.progress || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}
        
        <p className="text-sm text-gray-600">
          {uploadState.status === 'uploading' 
            ? `Upload progress: ${uploadState.progress}%`
            : isFullProcessing 
              ? `Full processing progress: ${videoData?.full?.progress || 0}%`
              : `Preview processing progress: ${videoData?.preview?.progress || 0}%`
          }
        </p>
        
        <div className="flex items-center justify-center text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
          <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Your video is encrypted and will be auto-deleted in 8 hours for privacy</span>
        </div>
      </div>
    );
  }, [videoData, uploadState.status, uploadState.progress, isPaymentComplete]);

  const renderPreview = useMemo(() => {
    // 🚀 PREVIEW MODE: Always use S3 URL (never blob)
    // 1. S3 URL + Subtitles + Not translating → Show video immediately
    // 2. Translating → Show loading (avoid video reload confusion)
    // 3. Missing video/subtitles → Show loading
    
    const s3VideoUrl = videoData?.preview?.s3_video_url || videoData?.full?.s3_video_url;
    const srtUrl = videoData?.preview?.s3_srt_url || videoData?.full?.s3_srt_url;
    const vttUrl = videoData?.preview?.s3_vtt_url || videoData?.full?.s3_vtt_url;
    
    // Determine effective video URL and loading state
    const hasS3Video = !!s3VideoUrl;
    const hasSubtitles = !!(srtUrl || vttUrl);
    
    let effectiveVideoUrl = '';
    let isLoading = false;
    
    if (hasS3Video && hasSubtitles) {
      // Case 1: S3 + Subtitles → Show immediately (tracks will update when translation done)
      effectiveVideoUrl = s3VideoUrl;
      isLoading = false;
      console.log('🎬 renderPreview - Using S3 URL (immediate preview)');
    } else {
      // Case 2: Missing video/subtitles → Show loading
      effectiveVideoUrl = '';
      isLoading = true;
      console.log('🎬 renderPreview - Waiting for video/subtitles');
    }
    
    console.log('🎬 renderPreview - Preview logic:', {
      hasS3Video,
      hasSubtitles,
      isTranslating,
      effectiveVideoUrl,
      isLoading
    });
    
    return (
      <div className="space-y-6">
        {/* 🚀 FLEXIBLE: Show different states based on availability */}
        {isLoading ? (
          <div className="flex items-center space-x-2 text-blue-600">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="font-medium">
              {isTranslating ? 'Translating subtitles...' : 'Processing video...'}
            </span>
          </div>
        ) : (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Preview Ready!</span>
          </div>
        )}
        
        {/* 🚀 FLEXIBLE: Show preview when we have video + subtitles */}
        {videoData?.videoId && effectiveVideoUrl && (srtUrl || vttUrl) && (
          <>
            <VideoPreview
              key={`preview-${videoData.videoId}-${effectiveVideoUrl}`}
              videoId={videoData.videoId}
              jobId={videoData.jobId}
              srtUrl={srtUrl}
              vttUrl={vttUrl} // 🚀 FLEXIBLE: VTT URL for VideoJS
              videoUrl={effectiveVideoUrl} // 🚀 FLEXIBLE: Use effective video URL
              {...({ fallbackVideoUrl: s3VideoUrl } as any)} // 🚀 FLEXIBLE: S3 fallback
            translatedSrtUrl={translatedSrtUrl}
            translatedVttUrl={translatedVttUrl} // 🚀 NEW: Translated VTT URL
            onStartFullProcessing={handleStartFullProcessing}
            videoData={videoData}
            hasClickedFullProcessing={hasClickedFullProcessing}
            isStartingFull={isStartingFull}
            isTranslating={isTranslating}
            onReset={() => {
              setUiState(prev => ({ ...prev, hasClickedFullProcessing: false }));
              setUploadState(prev => ({
                ...prev,
                status: 'idle',
                progress: 0
              }));
            }}
            uploadState={uploadState}
            onPaymentComplete={handlePaymentComplete}
          />
          </>
        )}
        

      </div>
    );
  }, [videoData, translatedSrtUrl, translatedVttUrl, hasClickedFullProcessing, isStartingFull, isTranslating, handleStartFullProcessing, handlePaymentComplete, uploadState]);

  const renderFullReady = useMemo(() => {
    const inFull = isFullContext;

    // 🚀 NEW: Full mode - prioritize Object URL (full video) over S3 URL
    const fullVideoUrl =
      uiState.localVideoUrl ||  // 🚀 NEW: Use Object URL for full video
      uploadState.fullVideoUrl ||
      (inFull ? (videoData?.full?.s3_video_url) : undefined);
    
    // 🚀 FLEXIBLE: Prefer local blob if available, fallback to S3
    const shouldUseLocalBlob = !!uiState.localVideoUrl;
    
    // 🚀 Log video source for debugging
    if (shouldUseLocalBlob) {
      console.log('🎬 renderFullReady - Using Object URL for full video playback');
    } else {
      console.log('🎬 renderFullReady - Using S3 URL for full video playback');
    }

    const fullSrtUrl =
      uploadState.fullSrtUrl ||
      (inFull ? (videoData?.full?.s3_srt_url) : undefined);
    
    const isDone = Boolean(fullVideoUrl && fullSrtUrl);
    
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2 text-green-600">
          <CheckCircle className="h-5 w-5" />
          <span className="font-medium">
            {isDone ? 'Full Video Ready!' : 'Full Video Processing...'}
          </span>
        </div>
        
        <p className="text-gray-600">
          {isDone ? 'Your complete subtitles is ready for download.' : 'Processing your complete video with subtitles...'}
        </p>
      
        {isDone && videoData && (
          <FullPlayer
            key={`full-${videoData.videoId}-${fullVideoUrl}`}
            videoUrl={shouldUseLocalBlob ? (uiState.localVideoUrl || '') : fullVideoUrl!}  // 🚀 FLEXIBLE: Use local blob if available
            videoType={uiState.localVideoType}     // 🚀 NEW: truyền MIME xuống
            translatedSrtUrl={translatedSrtUrl}
            srtUrl={fullSrtUrl}
            vttUrl={videoData?.full?.s3_vtt_url}   // 🚀 NEW: VTT subtitle URL
            translatedVttUrl={translatedVttUrl}    // 🚀 NEW: Translated VTT subtitle URL
            fallbackVideoUrl={videoData?.full?.s3_video_url} // 🚀 NEW: S3 fallback URL
            videoData={videoData}
            onSubtitleUpdate={(newSubtitleUrl: string) => {
              console.log('🌐 FileUpload - Subtitle updated from FullPlayer translation:', newSubtitleUrl);
              // 🚀 FIX: Update both SRT and VTT URLs based on the returned URL
              if (newSubtitleUrl.includes('.vtt')) {
                setUiState(prev => ({ ...prev, translatedVttUrl: newSubtitleUrl }));
              } else {
                setUiState(prev => ({ ...prev, translatedSrtUrl: newSubtitleUrl }));
              }
            }}
          />
        )}
      </div>
    );
  }, [isFullContext, videoData, uploadState, translatedSrtUrl, translatedVttUrl]); // 🚀 NEW: include translatedVttUrl

  const renderError = useMemo(() => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 text-red-600">
        <AlertCircle className="h-5 w-5" />
        <span className="font-medium">Error</span>
      </div>
      
      <p className="text-gray-600">
        {uploadState.error || 'An error occurred during processing.'}
      </p>
      
      <button
        onClick={handleRetry}
        className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        aria-label="Retry processing after error"
      >
        Try Again
      </button>
    </div>
  ), [uploadState.error, handleRetry]);

  // 🔧 Cờ "đang bận" - ưu tiên progress khi có active work
  const hasActiveWork = useMemo(() => {
    // đang upload → chắc chắn show progress
    if (uploadState.status === 'uploading') return true;

    // vừa thanh toán xong → show progress ngay
    if (isPaymentComplete) return true;

    // đã có jobId nhưng chưa có videoData do chưa poll xong → vẫn coi là đang bận
    if (uploadState.jobId && !videoData) return true;

    // nếu đã có videoData, check trạng thái preview/full
    if (videoData) {
      const ps = videoData.preview?.status;
      const fs = videoData.full?.status;
      
      // Preview đã completed → KHÔNG coi là đang bận
      if (ps === 'completed') return false;
      
      // Preview đang processing hoặc initial → coi là đang bận
      if (ps === 'processing' || ps === 'initial') return true;
      
      // Full đang processing → coi là đang bận
      if (fs === 'processing') return true;
    }

    return false;
  }, [uploadState.status, uploadState.jobId, isPaymentComplete, videoData]);

  // 🚀 SIMPLE: Logic đơn giản và rõ ràng
  const renderContent = useMemo(() => {
    // 🔧 Quan trọng: nếu đang bận → luôn hiển thị progress, KHÔNG rơi về form upload
    if (hasActiveWork) return renderUploadProgress;

    // Nếu có dữ liệu hoàn tất → render nội dung tương ứng
    const isFullCompleted = videoData?.full?.status === 'completed';
    const isPreviewCompleted = videoData?.preview?.status === 'completed';

    if (videoData) {
      if (isFullCompleted) return renderFullReady;
      if (isPreviewCompleted) return renderPreview;
    }

    // 🚀 FIX: Only show loading when we have a videoId but no videoData yet (URL access)
    // If no videoId, show upload form instead of loading
    if (videoData === null && uploadState.videoId) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2 text-blue-600">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="font-medium">Loading video data...</span>
          </div>
        </div>
      );
    }

    // Mặc định - show upload form when no videoId or no videoData
    return renderUploadArea;
  }, [hasActiveWork, videoData, uploadState.videoId, renderUploadArea, renderUploadProgress, renderPreview, renderFullReady]);

  return (
    <div className="w-full" ref={containerRef} data-file-upload-container>
      {recaptchaLoaded && !recaptchaScriptLoaded && (
        <GoogleRecaptcha
          onLoad={() => {
            setGlobalRecaptchaLoaded(true);
            setRecaptchaScriptLoaded(true);
          }}
          onError={(error) => {
            console.error('❌ reCAPTCHA failed to load:', error);
          }}
        />
      )}

      <div className="md:bg-white md:rounded-lg shadow-sm md:border p-2 md:p-6">
        {renderContent}
      </div>
      

      

      {/* Modal xác nhận start from begin */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center ${showStartFromBeginModal ? 'block' : 'hidden'}`}>
        <div className="bg-white rounded-lg p-6 max-w-md mx-4">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Start from beginning?</h3>
              <p className="text-sm text-gray-600">This will reset your current progress</p>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Warning:</p>
                <p>Starting over will:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Reset all current progress</li>
                  <li>Require re-uploading the file</li>
                  <li>Take additional processing time</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setUiState(prev => ({ ...prev, showStartFromBeginModal: false }))}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleStartFromBegin}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Yes, Start Over
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

 